import { ReactComponent as DisplayLogo } from "@/assets/dashboard/common/display.svg";
import { ReactComponent as EditIcon } from "@/assets/dashboard/common/edit.svg";
import { ReactComponent as LinkedinLogo } from "@/assets/dashboard/common/linkedin.svg";
import { ReactComponent as FacebookLogo } from "@/assets/dashboard/common/meta-min.svg";
import ControlledAutoComplete from "@/components/Form/InputFields/ControlledAutoComplete";
import PermissionGuard from "@/components/Guard/PermissionGuard";
import DeleteIcon from "@/components/Icon/DeleteIcon";
import Image from "@/components/Image/ImageSecondary";
import CustomImageLoader from "@/components/Loader/CustomImageLoader";
import CustomOverlapLoader from "@/components/Loader/CustomOverLapLoader";
import TableItemPlaceHolder from "@/components/Placeholder/TableItemPlaceHolder";
import CustomIconPopover from "@/components/Popover/CustomIconPopover";
import { StyledTableCell } from "@/components/Table/CustomTable";
import { MODULES } from "@/constants";
import { useDialogDispatch } from "@/context/DialogContext";
import { useSnackbarDispatch } from "@/context/SnackbarContext";
import useMediaFile from "@/hooks/useMediaFile";
import useScale from "@/hooks/useScale";
import routes from "@/routes";
import { Channel } from "@/types";
import correctTypedErrorMessage from "@/utils/correctTypedErrorMessage";
import parseJSON from "@/utils/parseJSON";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IconButton, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { QueryObserverResult } from "@tanstack/react-query";
import { capitalize } from "lodash";
import * as React from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import DisplayTemplate from "../../Template/DisplayTemplate";
import { convertColorFields } from "../../Template/helper";
import { useTemplateFieldsByTemplateMappingId } from "../../Template/template.hook";
import { TemplateVariant } from "../../Template/template.interface";
import {
  useDeleteAds,
  useGetBwCreativeDetails,
  useGetImageByUrn,
  useListAdCreativeApprovals,
} from "../asset.hook";
import {
  CreativeApiResponse,
  CreativeAssetType,
  CreativeByChannel,
  DisplayCreative,
  LinkedinCreative,
} from "../asset.interface";
import { isDisplayCreative } from "../asset.typeGuard";
import {
  CreativeType,
  getAssociatedLineItemsCount,
  getCreativeOptionsForTemplateView,
  getTemplateDataFromCreative,
  internalLinkedinCreativeToInternalCreativeMapping,
} from "../CreateOrEditAsset/data";
import { AdFormatTypeUnion } from "../CreateOrEditAsset/types";
import ImageAsset from "../ImageAsset";
import VendorApprovalCard from "../VendorApprovalCard";
import "./index.css";

const THUMBNAIL_WIDTH = 115;
const THUMBNAIL_HEIGHT = 36;
export default function AssetTableRow({
  row: creative,
  refetchCreatives,
  channel,
  removeCreative,
  headerLength,
}: AssetTableRowProps) {
  const { setDialogState } = useDialogDispatch();
  const { setSnackbarState } = useSnackbarDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryKeys = location.state?.queryKeys;
  const { data: creativeApprovals, isLoading: creativeApprovalLoading } =
    useListAdCreativeApprovals({
      queryParams: {
        advertiserId: creative?.advertiserId,
        creativeIds: [creative.id],
      },
    });
  const creativeApproved = creativeApprovals?.[0]?.approvals.some(
    (item: any) => item.approved
  );
  const assetInfo: any =
    creative && creative.assetInfo && typeof creative.assetInfo === "string"
      ? parseJSON(creative.assetInfo)
      : creative?.assetInfo;

  const template =
    isDisplayCreative(creative) &&
    creative.CreativeTemplateDataValueMapping?.CreativeTemplateVariant;

  const handleClickOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setDialogState({
      open: true,
      title: creative.name,
      children: template ? (
        <TemplateViewModal
          creativeData={[creative] as unknown as DisplayCreative[]}
        />
      ) : (
        <CreativeViewModal creativeData={[creative]} />
      ),

      styles: {
        ".MuiDialog-paper": {
          maxWidth: "max-content",
          minWidth:
            creative?.channel === "display" && template
              ? { xs: "auto", md: "800px" }
              : "auto",
        },
      },
    });
  };

  const handleOnEdit = (
    e: React.MouseEvent<HTMLButtonElement>,
    isEditAllowed: boolean
  ) => {
    e.stopPropagation();
    if (!isEditAllowed) return;
    navigate(
      `${routes.DASHBOARD}/${
        routes.ASSETS.CREATIVES.INDEX
      }/${routes.ASSETS.CREATIVES.EDIT(creative.id)}?channel=${channel}`,
      {
        state: { creative: JSON.stringify(creative), queryKeys },
      }
    );
  };
  const { mutateAsync: deleteAd, isLoading: deletingCreative } = useDeleteAds();

  const handleOnDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    e.stopPropagation();

    setDialogState({
      open: true,
      title: "Confirmation",
      variant: "alert",
      buttonName: "ok",
      buttonLoading: false,
      buttonFunction: () => deleteFunction(id),
      children: (
        <h4 style={{ margin: 0 }}>Are you sure you want to delete creative</h4>
      ),
    });
  };

  const deleteFunction = async (id: number) => {
    try {
      setDialogState((state) => {
        return {
          ...state,
          buttonLoading: true,
        };
      });
      await deleteAd({
        id,
        type: channel,
      });
      await refetchCreatives();
      removeCreative(id);
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: correctTypedErrorMessage(error),
      });
    } finally {
      setDialogState({
        open: false,
        title: "",
      });
    }
  };

  const channelLogo = () => {
    if (channel === "linkedin") {
      return <LinkedinLogo />;
    } else if (channel === "display") {
      return <DisplayLogo />;
    } else if (channel === "facebook") {
      return <FacebookLogo />;
    }
  };

  const editButtonState = getEditButtonState({
    channel: channel,
    isTemplateCreative: Boolean(template),
  });
  if (creativeApprovalLoading)
    return (
      <StyledTableCell colSpan={headerLength}>
        <TableItemPlaceHolder />
      </StyledTableCell>
    );
  return (
    <>
      <StyledTableCell align="left">{creative.name}</StyledTableCell>
      <StyledTableCell>{channelLogo()}</StyledTableCell>
      <StyledTableCell align="left">
        {getAssociatedLineItemsCount(creative)}
      </StyledTableCell>
      <StyledTableCell align="left">
        {assetInfo?.dimentions
          ? `${assetInfo?.dimentions?.width} x ${assetInfo?.dimentions?.height}`
          : "_"}
      </StyledTableCell>
      <StyledTableCell align="left">
        {creativeApprovals?.[0]?.approvals ? (
          <CustomIconPopover
            icon={creativeApproved ? CheckCircleIcon : CancelIcon}
            iconColor={creativeApproved ? "success" : "error"}
            triggerType="hover"
          >
            <VendorApprovalCard approvals={creativeApprovals?.[0]?.approvals} />
          </CustomIconPopover>
        ) : (
          <CancelIcon color="error" />
        )}
      </StyledTableCell>
      <StyledTableCell align="center">
        <Box
          sx={{
            width: `${THUMBNAIL_WIDTH}px`,
            height: `${THUMBNAIL_HEIGHT}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {channel === "display" && (
            <DisplayCreativeAsset<"thumbnail">
              key={creative.id}
              creative={creative as unknown as DisplayCreative}
              handleClickAsset={handleClickOpen}
              isThumbnail={true}
            />
          )}
          {channel === "linkedin" && (
            <LinkedinCreativeAsset<"thumbnail">
              key={creative.id}
              creative={creative as unknown as LinkedinCreative}
              handleClickAsset={handleClickOpen}
              isThumbnail={true}
            />
          )}
        </Box>
      </StyledTableCell>
      <StyledTableCell align="left">
        {getCreativeType({ ...creative, assetInfo }, channel)}
      </StyledTableCell>
      <StyledTableCell align="left">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PermissionGuard module={MODULES.CREATIVES} permissionType="update">
            <Tooltip title={editButtonState.tooltip}>
              <IconButton
                onClick={(e) => handleOnEdit(e, !editButtonState.disabled)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </PermissionGuard>
          <PermissionGuard module={MODULES.CREATIVES} permissionType="delete">
            <Tooltip title="Delete">
              <IconButton
                onClick={(e) => handleOnDelete(e, creative.id)}
                disabled={deletingCreative}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </PermissionGuard>
        </Box>
      </StyledTableCell>
    </>
  );
}

function getCreativeType(
  asset: any,
  type: "linkedin" | "display" | "facebook"
) {
  if (type === "display") {
    // VAST wrapper has no uploaded asset — identified by the tag URL.
    if (asset?.vastTagUrl) {
      return "VAST Wrapper";
    }
    if (asset?.CreativeTemplateDataValueMapping?.id) {
      return "JS Tag";
    }
    if (asset?.assetInfo && asset?.assetInfo?.type) {
      const assetType = asset?.assetInfo?.type;
      if (assetType.includes("text")) {
        return assetType?.split("/")?.[1];
      } else if (asset?.nativeOfferId) {
        return "native";
      } else {
        return asset?.assetInfo.mimeType;
      }
    } else if (asset?.creativeRuleKey && asset?.creativeContentTag) {
      return "JS Tag";
    }
  } else if (type === "facebook") {
    if (asset?.image_hash) {
      return CreativeType.IMAGE;
    } else if (asset?.video_id) {
      return CreativeType.VIDEO;
    }
  } else {
    return capitalize(
      internalLinkedinCreativeToInternalCreativeMapping[
        asset.type as AdFormatTypeUnion
      ]
    );
  }
}

interface AssetTableRowProps {
  row: CreativeByChannel<Channel>;
  selected: Array<any>;
  handleClickCheckBox: (event: React.MouseEvent<unknown>, name: string) => void;
  refetchCreatives: () => Promise<
    QueryObserverResult<CreativeApiResponse, unknown>
  >;
  channel: Channel;
  removeCreative: (creativeId: number) => void;
  headerLength: number;
}

const PreviewTemplate = (props: {
  template: TemplateVariant;
  templateValues: Record<string, any>;
  isOverlay?: boolean;
}) => {
  const { templateValues, template, isOverlay } = props;

  return (
    <DisplayTemplate
      isEdit={
        templateValues && Object.keys(templateValues)?.length > 0 ? true : false
      }
      isOverlay={false}
      values={templateValues}
      template={template.templateData}
      width={template.dimensions.width}
      height={template.dimensions.height}
    />
  );
};
export const PreviewTemplateThumbnail = (props: {
  template: TemplateVariant;
  templateValues: Record<string, any>;
  isOverlay?: boolean;
  thumbnailWidth: number;
  thumbnailHeight: number;
}) => {
  const {
    templateValues,
    template,
    isOverlay,
    thumbnailHeight,
    thumbnailWidth,
  } = props;
  const { ref: scaleRef } = useScale(thumbnailWidth, thumbnailHeight);

  return (
    <Box
      sx={{
        width: `${thumbnailWidth}px`,
        height: `${thumbnailHeight}px`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <Box
        ref={scaleRef}
        sx={{
          width: `calc(${template.dimensions.width}px + 2px)`,
          height: `calc(${template.dimensions.height}px + 2px)`,
        }}
      >
        <DisplayTemplate
          isEdit={
            templateValues && Object.keys(templateValues)?.length > 0
              ? true
              : false
          }
          isOverlay={isOverlay}
          values={templateValues}
          template={template.templateData}
          width={template.dimensions.width}
          height={template.dimensions.height}
        />
      </Box>
    </Box>
  );
};

export function TemplateViewModal({
  creativeData,
}: {
  creativeData: DisplayCreative[];
}) {
  const [selectedCreativeId, setSelectedCreativeId] = React.useState<string>(
    String(creativeData[0]?.id)
  );
  const selectedCreative = creativeData.find(
    (item) => String(item.id) === selectedCreativeId
  );

  const { template, templateDataValues, templateMappingId } =
    getTemplateDataFromCreative(selectedCreative);
  const [selectedTemplateValue, setSelectedTemplateValue] =
    React.useState<string>("BASE");

  const { data, isLoading } = useTemplateFieldsByTemplateMappingId({
    templateMappingId,
  });

  const allTemplateDataValues = data ?? templateDataValues ?? [];
  const base = allTemplateDataValues.find((item) => item.key === "BASE");
  const baseFields = base?.fields ? convertColorFields(base.fields) : null;
  if (!base || !baseFields) return <div>no data</div>;
  const fieldsExceptBase = allTemplateDataValues.filter(
    (item) => item.key !== "BASE"
  );
  const itemsObject = {
    [base.key as string]: {
      label: base.key,
      fields: baseFields,
    },
  };
  fieldsExceptBase.forEach((item) => {
    itemsObject[item.key as string] = {
      label: item.key as string,
      fields: item?.fields ? { ...baseFields, ...item.fields } : {},
    };
  });

  const itemList = Object.keys(itemsObject).map((key) => ({
    label: key,
    value: key,
  }));

  const handleResetSelectedTemplateValue = (creativeId: unknown) => {
    if (
      creativeId &&
      typeof creativeId === "string" &&
      creativeId !== selectedCreativeId &&
      selectedTemplateValue !== "BASE"
    ) {
      setSelectedTemplateValue("BASE");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        height: "100%",
        flex: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ width: "350px" }}>
          <ControlledAutoComplete
            label={"Variant"}
            value={selectedCreativeId}
            setValue={setSelectedCreativeId}
            itemList={getCreativeOptionsForTemplateView(creativeData)}
            customOnChange={handleResetSelectedTemplateValue}
          />
        </Box>
        <Box sx={{ width: "350px" }}>
          <ControlledAutoComplete
            label={"Ad Copy"}
            value={selectedTemplateValue}
            setValue={setSelectedTemplateValue}
            itemList={itemList}
            loading={isLoading}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pb: 2,
        }}
      >
        <CustomOverlapLoader isLoading={!template}>
          {template ? (
            <PreviewTemplate
              template={template}
              templateValues={itemsObject[selectedTemplateValue].fields}
              isOverlay={false}
            />
          ) : (
            <></>
          )}
        </CustomOverlapLoader>
      </Box>
    </Box>
  );
}

export function CreativeViewModal({
  creativeData,
}: {
  creativeData: CreativeByChannel<Channel>[];
}) {
  const selectedCreative = creativeData[0];

  if (!selectedCreative) return <div>no data</div>;

  const creativeChannel = selectedCreative.channel;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        height: "100%",
        maxHeight: "calc(100vh - 200px)",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflowY: "scroll",
        }}
      >
        <CustomOverlapLoader isLoading={!selectedCreative}>
          <>
            {selectedCreative && (
              <>
                {creativeChannel === "display" && (
                  <DisplayCreativeAsset
                    creative={selectedCreative as unknown as DisplayCreative}
                    handleClickAsset={undefined}
                    isThumbnail={false}
                  />
                )}
                {creativeChannel === "linkedin" && (
                  <LinkedinCreativeAsset
                    creative={selectedCreative as unknown as LinkedinCreative}
                    isThumbnail={false}
                    handleClickAsset={undefined}
                  />
                )}
              </>
            )}
          </>

          {/* <Box>
            {" "}
            {selectedCreative?.channel === "display" ? (
              <iframe
                src={imagesData?.[0]?.previewUrl}
                style={{
                  width: `calc(${assetInfo?.dimentions?.width}px + 10px)`,
                  height: `calc(${assetInfo?.dimentions?.height}px + 10px)`,
                }}
              ></iframe>
            ) : selectedCreative?.assetUrl ? (
              <iframe
                height={"500px"}
                width={"100%"}
                src={selectedCreative?.assetUrl}
              ></iframe>
            ) : (
              <Carousel
                showThumbs={false}
                swipeable={true}
                showIndicators={false}
                showStatus={false}
                className="carousel"
              >
                {imagesData.map((item, i) => (
                  <Image
                    key={i}
                    img={item.image}
                    alt={"image"}
                    loadedStatus={item.loadedStatus ?? "error"}
                  />
                ))}
              </Carousel>
            )}
          </Box> */}
        </CustomOverlapLoader>
      </Box>
    </Box>
  );
}

function getEditButtonState({
  channel,
  isTemplateCreative,
}: {
  channel: Channel;
  isTemplateCreative: boolean;
}) {
  if (channel !== "display" || isTemplateCreative) {
    return {
      disabled: true,
      tooltip: "You can't edit this creative",
    };
  }
  return {
    disabled: false,
    tooltip: "Edit",
  };
}

function DisplayCreativeAsset<
  Type extends "thumbnail" | "original-asset" = "original-asset"
>({
  creative,
  handleClickAsset,
  isThumbnail = false,
}: {
  creative: CreativeByChannel<"display">;
  handleClickAsset: Type extends "thumbnail"
    ? (e: React.MouseEvent<HTMLDivElement>) => void
    : undefined;
  isThumbnail?: boolean;
}) {
  const {
    adId,
    assetInfo = null,
    CreativeTemplateDataValueMapping = null,
  } = creative;

  const type: CreativeAssetType = CreativeTemplateDataValueMapping?.id
    ? "templateAd"
    : assetInfo?.mimeType === "video"
    ? "video"
    : "image";

  const {
    data: adDetails,
    isLoading,
    isFetching,
  } = useGetBwCreativeDetails({
    id: adId,
    options: {
      enabled: Boolean(type !== "templateAd" || !!adId),
      retry: 2,
      retryDelay: 5000,
    },
  });

  const { mediaData, error } = useMediaFile(
    type === "video" ? adDetails?.previewUrl : null
  );

  const templateValues = convertColorFields(
    CreativeTemplateDataValueMapping?.CreativeTemplateDataValues?.[0]?.fields
  );
  const containerThumbnailStyles = isThumbnail
    ? {
        cursor: "pointer",
        "&:hover": {
          transform: "scale(1.1)",
        },
      }
    : {};

  const ImageComponent = isThumbnail ? ThumbnailImageAsset : ImageAsset;
  const VideoComponent = isThumbnail ? ThumbnailVideoAsset : VideoAsset;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        ...containerThumbnailStyles,
      }}
      onClick={handleClickAsset}
    >
      {isThumbnail &&
        type === "templateAd" &&
        CreativeTemplateDataValueMapping && (
          <PreviewTemplateThumbnail
            template={CreativeTemplateDataValueMapping.CreativeTemplateVariant}
            templateValues={templateValues}
            thumbnailWidth={THUMBNAIL_WIDTH}
            thumbnailHeight={THUMBNAIL_HEIGHT}
            isOverlay
          />
        )}
      {type === "image" && (
        <ImageComponent
          asset={{
            name: creative.name,
            image: isThumbnail
              ? adDetails?.thumbnailUrl
              : adDetails?.previewUrl,
            width: adDetails?.width ?? creative.assetInfo?.dimentions?.width,
            height: adDetails?.height ?? creative.assetInfo?.dimentions?.height,
          }}
          isLoadingAssetDetails={isLoading && isFetching}
        />
      )}
      {type === "video" &&
        (mediaData?.url || adDetails?.isPreviewLinkGenerated) && (
          <VideoComponent
            asset={{
              name: creative.name,
              image: adDetails?.thumbnailUrl,
              video: adDetails?.isPreviewLinkGenerated
                ? adDetails?.previewUrl
                : mediaData?.url ?? "",
            }}
            isLoadingAssetDetails={isLoading && isFetching}
          />
        )}
    </Box>
  );
}
function LinkedinCreativeAsset<
  Type extends "thumbnail" | "original-asset" = "original-asset"
>({
  creative,
  handleClickAsset,
  isThumbnail,
}: {
  creative: CreativeByChannel<"linkedin">;
  handleClickAsset: Type extends "thumbnail"
    ? (e: React.MouseEvent<HTMLDivElement>) => void
    : undefined;
  isThumbnail: Type extends "thumbnail" ? true : false;
}) {
  const { linkedinAssetUrn = "", assetInfo, advertiserId } = creative;
  const type: CreativeAssetType =
    assetInfo?.mimeType === "video" ? "video" : "image";

  const { data, isLoading } = useGetImageByUrn({
    id: linkedinAssetUrn,
    queryParams: {
      advertiserId,
    },
  });
  const ImageComponent = isThumbnail ? ThumbnailImageAsset : ImageAsset;
  const VideoComponent = isThumbnail ? ThumbnailVideoAsset : VideoAsset;

  const containerThumbnailStyles = isThumbnail
    ? {
        cursor: "pointer",
        "&:hover": {
          transform: "scale(1.1)",
        },
      }
    : {};
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        ...containerThumbnailStyles,
      }}
      onClick={handleClickAsset}
    >
      {type === "image" && (
        <ImageComponent
          asset={{
            name: creative.name,
            image: data?.downloadUrl,
            width: creative?.assetInfo?.dimentions?.width,
            height: creative?.assetInfo?.dimentions?.height,
          }}
          isLoadingAssetDetails={isLoading}
        />
      )}
      {type === "video" && (
        <VideoComponent
          asset={{
            name: creative.name,
            video: data?.downloadUrl,
            image: data?.thumbnail,
          }}
          isLoadingAssetDetails={isLoading}
        />
      )}
    </Box>
  );
}

function ThumbnailImageAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: {
    image: string;
    name: string;
    width: number | undefined;
    height: number | undefined;
  };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={THUMBNAIL_WIDTH}
      height={THUMBNAIL_HEIGHT}
      isLoading={isLoadingAssetDetails}
    >
      <Image
        img={asset.image}
        alt={asset.name}
        maxWidth={`${THUMBNAIL_WIDTH}px`}
        maxHeight={`${THUMBNAIL_HEIGHT}px`}
      />
    </CustomImageLoader>
  );
}

function VideoAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: { image: string; name: string; video: string };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={500}
      height={500}
      isLoading={isLoadingAssetDetails}
    >
      <video width={500} poster={asset.image} preload="auto" controls>
        <source src={asset.video} type="video/mp4" />
      </video>
    </CustomImageLoader>
  );
}
function ThumbnailVideoAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: { image: string; name: string; video: string };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={THUMBNAIL_WIDTH}
      height={THUMBNAIL_HEIGHT}
      isLoading={isLoadingAssetDetails}
    >
      <video
        width={THUMBNAIL_WIDTH}
        height={THUMBNAIL_HEIGHT}
        poster={asset.image}
        preload="none"
      >
        <source src={asset.video} type="video/mp4" />
      </video>
    </CustomImageLoader>
  );
}
